import type {
  GuessCategoryId,
  MysteryVariantId,
  SkrivelegBriefing,
  SkrivelegGameId,
  SkrivelegGuessResult,
  SkrivelegPersuadeResultView,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

export type SkrivelegApiMode = 'live' | 'mock';

export interface SkrivelegStartResult {
  sessionToken: string;
  game: SkrivelegGameId;
  mysteryVariant?: MysteryVariantId;
  briefing: SkrivelegBriefing;
  openingMessage?: string;
  sessionDurationMs: number;
  maxUserMessages: number;
  apiMode: SkrivelegApiMode;
}

export interface SkrivelegChatResult {
  reply: string;
  apiMode: SkrivelegApiMode;
  userMessageCount: number;
  maxUserMessages: number;
  remainingMs: number;
  done: boolean;
  convinced?: boolean;
  skepticism?: number;
  aiGuess?: string;
  guessResult?: SkrivelegGuessResult;
}

export interface SkrivelegGuessResponse extends SkrivelegGuessResult {
  apiMode: SkrivelegApiMode;
}

export interface SkrivelegHintResult {
  hint: string;
  hintsRemaining: number;
  apiMode: SkrivelegApiMode;
}

export async function fetchSkrivelegHealth(): Promise<SkrivelegApiMode | null> {
  try {
    const res = await fetch(`${API_BASE}/api/health`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { skriveleg?: SkrivelegApiMode };
    return data.skriveleg === 'live' || data.skriveleg === 'mock'
      ? data.skriveleg
      : null;
  } catch {
    return null;
  }
}

const SKRIVELEG_FETCH_MS = 35_000;

async function postJson<T>(
  path: string,
  body: unknown,
  errorFallback: string,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SKRIVELEG_FETCH_MS);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e instanceof Error && e.name === 'AbortError') {
      throw new Error('Det tog for lang tid — tjek forbindelsen og prøv igen.');
    }
    throw new Error(
      'Kunne ikke kontakte serveren. Kør med VITE_USE_API=true og start server.',
    );
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? errorFallback);
  }

  return (await res.json()) as T;
}

export async function fetchSkrivelegStart(body: {
  game: SkrivelegGameId;
  mysteryVariant?: MysteryVariantId;
  guessCategoryId?: GuessCategoryId;
}): Promise<SkrivelegStartResult> {
  return postJson<SkrivelegStartResult>(
    '/api/skriveleg/start',
    body,
    'Kunne ikke starte Skriveleg.',
  );
}

export async function fetchSkrivelegChat(body: {
  sessionToken: string;
  message: string;
}): Promise<SkrivelegChatResult> {
  return postJson<SkrivelegChatResult>(
    '/api/skriveleg/chat',
    body,
    'Kunne ikke sende besked.',
  );
}

export async function fetchSkrivelegGuess(body: {
  sessionToken: string;
  guess: string;
}): Promise<SkrivelegGuessResponse> {
  return postJson<SkrivelegGuessResponse>(
    '/api/skriveleg/guess',
    body,
    'Kunne ikke tjekke gættet.',
  );
}

export async function fetchSkrivelegHint(body: {
  sessionToken: string;
}): Promise<SkrivelegHintResult> {
  return postJson<SkrivelegHintResult>(
    '/api/skriveleg/hint',
    body,
    'Kunne ikke hente hint.',
  );
}

export async function fetchSkrivelegForfeit(body: {
  sessionToken: string;
}): Promise<
  SkrivelegGuessResponse | (SkrivelegPersuadeResultView & { apiMode: SkrivelegApiMode })
> {
  return postJson(
    '/api/skriveleg/forfeit',
    body,
    'Kunne ikke afslutte legen.',
  );
}
